export const EDUCATOR_ROLES = ['tutor', 'parent'];

/** Legacy: учительская роль сведена к репетиторской. */
export const normalizeEducatorRole = (role) => (role === 'teacher' ? 'tutor' : role);

/**
 * Кабинеты и регистрация наставников.
 * По умолчанию включено; явно отключить: REACT_APP_EDUCATOR_SUITE_ENABLED=false
 */
const _suiteEnv = String(process.env.REACT_APP_EDUCATOR_SUITE_ENABLED ?? '').toLowerCase();
export const EDUCATOR_SUITE_ENABLED =
  _suiteEnv === '' || _suiteEnv === 'true' || _suiteEnv === '1';

/**
 * Публичная регистрация в роли репетитора на /register.
 */
const _pubEducatorEnv = String(process.env.REACT_APP_ENABLE_PUBLIC_EDUCATOR_SIGNUP ?? '').toLowerCase();
export const PUBLIC_EDUCATOR_SIGNUP_ENABLED =
  EDUCATOR_SUITE_ENABLED &&
  (_pubEducatorEnv === '' || _pubEducatorEnv === 'true' || _pubEducatorEnv === '1');

export const isEducatorRole = (role) => EDUCATOR_ROLES.includes(normalizeEducatorRole(role));

export const getEducatorRouteBase = (role) => {
  const r = normalizeEducatorRole(role);
  if (r === 'parent') return '/parent';
  return '/tutor';
};

export const getEducatorInvitationsRoute = (role) => `${getEducatorRouteBase(role)}/invitations`;

export const getRoleLabel = (role) => {
  const r = normalizeEducatorRole(role);
  if (r === 'parent') return 'Родитель';
  if (r === 'tutor') return 'Репетитор';
  if (role === 'admin') return 'Администратор';
  return 'Ученик';
};

export const getPreparationModeLabel = (mode) => {
  if (mode === 'teacher' || mode === 'tutor') return 'С репетитором';
  if (mode === 'parent') return 'С родителем';
  return 'Самостоятельно';
};

export const getPreparationModeDescription = (mode) => {
  if (mode === 'teacher' || mode === 'tutor') return 'Отправим запрос по коду репетитора';
  if (mode === 'parent') return 'Отправим запрос по коду родителя';
  return 'Без привязки к наставнику';
};

export const normalizePreparationMode = (mode) => {
  if (mode === 'teacher') return 'tutor';
  return mode;
};

export const getEducatorLabel = (role, form = 'nominative') => {
  const r = normalizeEducatorRole(role);
  if (r === 'parent') {
    if (form === 'genitive') return 'родителя';
    if (form === 'dative') return 'родителю';
    if (form === 'instrumental_plural') return 'родителями';
    return 'родитель';
  }
  if (form === 'genitive') return 'репетитора';
  if (form === 'dative') return 'репетитору';
  if (form === 'instrumental_plural') return 'репетиторами';
  return 'репетитор';
};

export const getEducatorTitle = (role) => {
  const r = normalizeEducatorRole(role);
  if (r === 'parent') return 'Родитель';
  return 'Репетитор';
};

export const getLinkedEducatorRole = (status, user) => {
  if (status?.linked_tutor?.role) return normalizeEducatorRole(status.linked_tutor.role);
  if (status?.requests?.length) {
    const latestRole = status.requests.find((request) => request?.tutor?.role)?.tutor?.role;
    if (latestRole) return normalizeEducatorRole(latestRole);
  }
  if (user?.preparation_mode === 'teacher' || user?.preparation_mode === 'tutor') return 'tutor';
  if (user?.preparation_mode === 'parent') return 'parent';
  return 'tutor';
};

/** Ссылка для регистрации ученика/ребёнка с кодом наставника. */
export const buildEducatorShareLink = (code, role) => {
  const mode = normalizeEducatorRole(role) === 'parent' ? 'parent' : 'tutor';
  if (typeof window === 'undefined') return `/register?ref=${code}&mode=${mode}`;
  return `${window.location.origin}/register?ref=${encodeURIComponent(code)}&mode=${mode}`;
};

const capitalizeWord = (word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word);

/**
 * @param {'nominative'|'genitive'|'instrumental'} caseForm — падеж (именительный / родительный / творительный)
 * @param {boolean} capitalize — заглавная буква (для пунктов меню и заголовков)
 */
export const getLearnerLabel = (
  role,
  { plural = false, nominative = false, instrumental = false, capitalize = false } = {},
) => {
  const isParent = normalizeEducatorRole(role) === 'parent';
  let label;
  if (instrumental) {
    label = isParent ? (plural ? 'детьми' : 'ребёнком') : plural ? 'учениками' : 'учеником';
  } else if (nominative) {
    label = isParent ? (plural ? 'дети' : 'ребёнок') : plural ? 'ученики' : 'ученик';
  } else {
    label = isParent ? (plural ? 'детей' : 'ребёнка') : plural ? 'учеников' : 'ученика';
  }
  return capitalize ? capitalizeWord(label) : label;
};

/** Подпись раздела «ученики / дети» в навигации кабинета. */
export const getLearnerNavLabel = (role) => {
  if (isParentRole(role)) return 'Мои дети';
  return getLearnerLabel(role, { nominative: true, plural: true, capitalize: true });
};

export const isParentRole = (role) => normalizeEducatorRole(role) === 'parent';

/** Имя по умолчанию в карточках (Ученик / Ребёнок). */
export const getLearnerDefaultName = (role) =>
  isParentRole(role) ? 'Ребёнок' : getLearnerLabel(role, { nominative: true, capitalize: true });

/** «Выберите ученика» / «Выберите ребёнка». */
export const formatSelectLearner = (role) => `Выберите ${getLearnerLabel(role)}`;

/** «Поиск учеников...» / «Поиск детей...». */
export const formatLearnerSearchPlaceholder = (role) =>
  isParentRole(role) ? 'Поиск детей...' : `Поиск ${getLearnerLabel(role, { plural: true })}...`;

/** «Пока нет учеников» / «Пока нет детей». */
export const formatLearnerEmptyState = (role) =>
  isParentRole(role) ? 'Пока нет детей' : `Пока нет ${getLearnerLabel(role, { plural: true })}`;

/** «Подключённые ученики» / «Подключённые дети». */
export const formatLearnerConnectedHeading = (role) =>
  isParentRole(role) ? 'Подключённые дети' : 'Подключённые ученики';

/** «Учеников на тарифе» / «Детей на тарифе». */
export const formatLearnerOnPlanLabel = (role) =>
  `${getLearnerLabel(role, { plural: true, capitalize: true })} на тарифе`;

/** «Новые заявки учеников» / «Новые заявки детей». */
export const formatLearnerRequestsHeading = (role) =>
  `Новые заявки ${getLearnerLabel(role, { plural: true })}`;

/** «К ученикам» / «К детям». */
export const formatLearnerToLink = (role) => (isParentRole(role) ? 'К детям' : 'К ученикам');

/** «Все ученики» / «Все дети». */
export const formatAllLearnersLabel = (role) =>
  isParentRole(role) ? 'Все дети' : `Все ${getLearnerLabel(role, { nominative: true, plural: true })}`;

/** Набор подписей для кабинета наставника / родителя. */
export const getLearnerPhrases = (role) => ({
  nav: getLearnerNavLabel(role),
  nominative: getLearnerLabel(role, { nominative: true, capitalize: true }),
  nominativeLower: getLearnerLabel(role, { nominative: true }),
  nominativePlural: getLearnerLabel(role, { nominative: true, plural: true, capitalize: true }),
  genitive: getLearnerLabel(role),
  genitivePlural: getLearnerLabel(role, { plural: true }),
  genitivePluralCap: getLearnerLabel(role, { plural: true, capitalize: true }),
  instrumentalPlural: getLearnerLabel(role, { plural: true, instrumental: true }),
  defaultName: getLearnerDefaultName(role),
  select: formatSelectLearner(role),
  search: formatLearnerSearchPlaceholder(role),
  empty: formatLearnerEmptyState(role),
  connected: formatLearnerConnectedHeading(role),
  onPlan: formatLearnerOnPlanLabel(role),
  requests: formatLearnerRequestsHeading(role),
  toLink: formatLearnerToLink(role),
  all: formatAllLearnersLabel(role),
});
